import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useParams } from "react-router";

import useCurrentTeam from "../hooks/useCurrentTeam";
import { RosterContextProps, RosterContextValues } from "../types/contexts";

import {
    selectSelectedLeagueTeam,
    selectSelectedTeam,
} from "../../../../../features/clubhouse/clubhouseSlice";
import { selectSetting } from "../../../../../features/global/globalSlice";
import { useAppSelector } from "../../../../../hooks/reduxHooks";
import { IPlayer, ISiteSetting } from "../../../../../types/api";

const RosterContext = createContext<RosterContextValues | null>(null);

export const useRosterContext = () => {
    const context = useContext(RosterContext);
    if (!context) {
        throw new Error(
            "useRosterContext must be used within a RosterProvider",
        );
    }
    return context;
};

export const RosterProvider: React.FC<RosterContextProps> = props => {
    const { children, initialRoster, teamId } = props;
    const [roster, setRoster] = useState<IPlayer[]>([]);
    const [sending, setSending] = useState<boolean>(false);
    const team = useAppSelector(selectSelectedTeam);
    const leagueTeam = useAppSelector(selectSelectedLeagueTeam);
    const params = useParams();

    useEffect(() => {
        setRoster(initialRoster);
    }, [initialRoster]);

    const sortedRoster = useMemo(() => {
        return roster.sort((a, b) => {
            const aNum = a.uniformNumber;
            const bNum = b.uniformNumber;

            if (aNum && bNum && aNum !== bNum) {
                return aNum - bNum;
            } else if (aNum) {
                return 1;
            } else if (bNum) {
                return -1;
            }

            const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
            const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
            return nameA.localeCompare(nameB);
        });
    }, [roster]);

    const player = useMemo(() => {
        return params.playerId
            ? roster.find(
                  p => params.playerId && p.id === parseInt(params.playerId),
              )
            : undefined;
    }, [roster, params]);

    const currentYear = parseInt(
        useAppSelector(state =>
            selectSetting(state, "majorybl_current_league_year"),
        )?.setting,
    );

    const currentTeam =
        teamId && team?.id === parseInt(teamId) ? team : leagueTeam;

    const { currentTeamsLeague, currentTeamAgeLevel } = useCurrentTeam(
        currentTeam,
        currentYear,
    );

    const rosterCount = roster.filter(r => r.active === 1).length;

    const hsPlayoffDate = useAppSelector(state =>
        selectSetting(state, "majorybl_highschool_playoff_eligibility_date"),
    )?.setting;
    const regularPlayoffDate = useAppSelector(state =>
        selectSetting(state, "majorybl_playoff_eligibility_date"),
    )?.setting;

    const playoffEligibileDate: ISiteSetting["setting"] =
        currentTeamAgeLevel?.season.season === "High School"
            ? hsPlayoffDate
            : regularPlayoffDate;

    const contextValue = useMemo(
        () => ({
            roster: sortedRoster,
            player,
            setRoster,
            sending,
            setSending,
            currentTeam,
            currentTeamAgeLevel,
            currentTeamsLeague,
            currentYear,
            rosterCount,
            playoffEligibileDate,
        }),
        [sortedRoster, sending, teamId, params, player],
    );

    return (
        <RosterContext.Provider value={contextValue}>
            {children}
        </RosterContext.Provider>
    );
};
